import { NgTemplateOutlet } from '@angular/common'
import {
	AfterViewInit,
	booleanAttribute,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	effect,
	ElementRef,
	inject,
	InjectionToken,
	input,
	output,
	signal,
	viewChild,
} from '@angular/core'
import { RouterLink } from '@angular/router'
import { AppConfigService } from '@mos/client/shared/app-config'
import { CanvasEmitterComponent } from '@mos/client/shared/canvas/components/canvas-emitter.component'
import { EmitterConfig } from '@mos/client/shared/canvas/models/emitter.config'
import { isServer, LEAVE_SYNC_CLASS, LeaveSyncDirective } from '@mos/client/shared/utils'
import { ClickClassDirective } from '../../directives/click-class.directive'
import { LongPressDirective } from '../../directives/long-press.directive'
import { TooltipDirective } from '../../directives/tooltip.directive'
import { burstParticleConfig, particleConfig } from './button.particles'

/**
 * Whether buttons show their star particles (ambient stars on the border, burst on click). Games can opt out by
 * providing `false`.
 */
export const BUTTON_STARS = new InjectionToken<boolean>('BUTTON_STARS', { factory: () => true })

@Component({
	imports: [
		RouterLink,
		ClickClassDirective,
		LongPressDirective,
		TooltipDirective,
		CanvasEmitterComponent,
		NgTemplateOutlet,
	],
	selector: 'mos-button',
	hostDirectives: [LeaveSyncDirective],
	// Button is a widely-reused leaf component, nested inside many other components' templates - proactively
	// syncing its leave with LeaveSyncDirective (rather than a plain animate.leave string binding) means any
	// future ancestor that also adopts LeaveSyncDirective gets Button's own leave animation synced for free,
	// instead of rediscovering the cross-component-boundary gap ChapterTitle/Progress hit (see
	// docs/animations-migration.md).
	providers: [{ provide: LEAVE_SYNC_CLASS, useValue: 'button-out' }],
	templateUrl: './button.component.html',
	styleUrls: ['./button.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: {
		'animate.enter': 'button-in',
	},
})
export class ButtonComponent implements AfterViewInit {
	isMobileApp = signal(inject(AppConfigService).isMobileApp())
	private readonly starsEnabled = inject(BUTTON_STARS)
	particleEmitterConfig?: EmitterConfig

	private changeDetectorRef = inject(ChangeDetectorRef)
	private hostElement = inject(ElementRef)

	private boundingRect?: DOMRect

	readonly stars = viewChild<CanvasEmitterComponent>('stars')

	public readonly type = input<'button' | 'submit' | 'reset' | 'link'>('button')
	public readonly disabled = input(false, { transform: booleanAttribute })
	public readonly loading = input(
		false,
		/**
		 * Not compatible with 'link' type
		 */
		// @HostBinding('class.longPress')
		{ transform: booleanAttribute },
	)

	/**
	 * Not compatible with 'link' type
	 */
	public readonly longPress = input(false, { transform: booleanAttribute })

	// Disabled until really needed - longPress's value is intentionally never read.
	protected _longPress = false

	protected progress = 0

	public readonly commands = input<string[] | string>()
	protected readonly burstParticleConfig = burstParticleConfig

	protected errorMessage = ''

	public readonly mosClick = output()

	public readonly error = input('')

	constructor() {
		effect(() => {
			this.errorMessage = this.error()
			// Trigger change detection on next cycle to allow the ellipsis condition to be evaluated and applied
			setTimeout(() => this.changeDetectorRef.markForCheck(), 0)
		})
	}

	click() {
		// Only measured when the stars are enabled (see BUTTON_STARS): the click is emitted either way
		if (this.boundingRect) {
			this.stars()?.burst(50, {
				...burstParticleConfig,
				displayArea: { width: this.boundingRect.width + 40, height: this.boundingRect.height + 40 },
			})
		}

		this.mosClick.emit()
	}

	ngAfterViewInit() {
		if (isServer || !this.starsEnabled) {
			return
		}

		const buttonElement =
			this.type() === 'link'
				? this.hostElement.nativeElement.querySelector('a')
				: this.hostElement.nativeElement.querySelector('button')
		if (!buttonElement) {
			return
		}
		this.boundingRect = buttonElement.getBoundingClientRect()
		if (!this.boundingRect) {
			return
		}
		this.particleEmitterConfig = {
			...particleConfig,
			displayArea: { width: this.boundingRect.width + 40, height: this.boundingRect.height + 40 },
		}
		this.changeDetectorRef.detectChanges() // markForCheck is not enough to trigger the change detection
	}
}
